import type { SchemaRecord } from '../shared/schema-input.js';
import { isObject, isString } from '../shared/runtime-values.js';
import { ol } from './ol-map.js';
import type View from 'ol/View.js';

interface IpGeoState {
  IP_GEO_CENTER_STORAGE_KEY: string;
  hasSharedMapState: boolean;
  hasMeaningfulActionForAutoCenter: boolean;
  olView: View;
  onCountry?: (country: string | null) => void;
}

type IpGeoTrack = (event: 'ip_geo_center_failed', props: IpGeoFailureProperties) => void;

interface IpGeoFailureProperties {
  reason: string;
  error_blob: string;
  http_status?: string;
}

export function initIpGeoCenter(state: IpGeoState, track?: IpGeoTrack) {
  function reportFailure(reason: string, cause: unknown, status?: number): void {
    const message = cause instanceof Error ? cause.message : String(cause);
    console.warn('IP map centering failed:', message);
    const failure: IpGeoFailureProperties = {
      reason,
      error_blob: JSON.stringify({ message }).slice(0, 4096),
    };
    if (status) failure.http_status = String(status);
    track?.('ip_geo_center_failed', failure);
  }

  function readCachedIpGeoCenter(): { lat: number; lng: number; country: string | null } | null {
    try {
      const raw = sessionStorage.getItem(state.IP_GEO_CENTER_STORAGE_KEY);
      if (!raw) return null;
      const data: SchemaRecord = JSON.parse(raw);
      // SAFETY: The adjacent primitive and range check validates this value before use.
      return (data && isObject(data) && Number.isFinite(data.lat as number) && Number.isFinite(data.lng as number)) ? { lat: data.lat as number, lng: data.lng as number, country: isString(data.country) ? data.country : null } : null;
    } catch { return null; }
  }

  function writeCachedIpGeoCenter(latLng: { lat: number; lng: number; country: string | null }) {
    try { sessionStorage.setItem(state.IP_GEO_CENTER_STORAGE_KEY, JSON.stringify(latLng)); } catch {}
  }

  async function autoCenterBlankMapFromIp(): Promise<boolean> {
    if (document.body.dataset.initialLat) return false;
    if (state.hasSharedMapState || state.hasMeaningfulActionForAutoCenter) return false;
    const cached = readCachedIpGeoCenter();
    if (cached && (!state.onCountry || cached.country)) { state.onCountry?.(cached.country); state.olView.setCenter(ol.proj.fromLonLat([cached.lng, cached.lat])); state.olView.setZoom(11); return true; }
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 1200);
    try {
      const res = await fetch('/api/geo', { headers: { accept: 'application/json' }, signal: controller.signal });
      if (!res.ok) {
        reportFailure('http_error', new Error(`IP geolocation returned HTTP ${res.status}`), res.status);
        return false;
      }
      const data: SchemaRecord = await res.json();
      const lat = Number(data?.lat), lng = Number(data?.lng);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        reportFailure('invalid_response', new Error('IP geolocation response has invalid coordinates'));
        return false;
      }
      const country = isString(data.country) ? data.country : null;
      state.onCountry?.(country);
      if (state.hasSharedMapState || state.hasMeaningfulActionForAutoCenter) return false;
      writeCachedIpGeoCenter({ lat, lng, country });
      state.olView.setCenter(ol.proj.fromLonLat([lng, lat])); state.olView.setZoom(11);
      return true;
    } catch (error) {
      reportFailure(controller.signal.aborted ? 'timeout' : error instanceof SyntaxError ? 'response_read_error' : 'network_error', error);
      return false;
    } finally {
      clearTimeout(timeout);
    }
  }

  return { autoCenterBlankMapFromIp };
}
